import { useEffect, useMemo, useState } from 'react';
import { CircleNotch, WarningDiamond } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { mfgApi } from '@/pages/mfg/mfgApi';
import { mktApi, errMsg } from './marketingApi';
import { yen } from './mktShared';
import { todayJst } from '@/utils/jstDate';

const round = (n) => Math.round(n);

export default function EstimateDialog({ open, onOpenChange, order, settings, onSaved }) {
  const [marginPct, setMarginPct] = useState(30);
  const [taxRate, setTaxRate] = useState(10);
  const [rows, setRows] = useState([]); // {line_id, item_id, name, sku, qty, uom, cost, price('' = auto)}
  const [plMap, setPlMap] = useState({}); // item_id → 販売単価マスタ (本日有効)
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !order?.customer_id) return;
    mktApi.priceList(order.customer_id).then((list) => {
      const today = todayJst();
      const map = {};
      (list || [])
        .filter((p) => (!p.valid_from || p.valid_from <= today) && (!p.valid_to || p.valid_to >= today))
        .sort((a, b) => (a.valid_from || '').localeCompare(b.valid_from || ''))
        .forEach((p) => { map[p.item_id] = p.unit_price; });
      setPlMap(map);
    }).catch(() => setPlMap({}));
  }, [open, order]);

  useEffect(() => {
    if (!open || !order) return;
    const est = order.estimate;
    setMarginPct(est?.margin_pct ?? settings?.default_margin_pct ?? 30);
    setTaxRate(est?.tax_rate ?? settings?.tax_rate ?? 10);
    const estMap = Object.fromEntries((est?.lines || []).map((l) => [l.line_id, l]));
    setLoading(true);
    Promise.all((order.lines || []).map(async (ln) => {
      const el = estMap[ln.line_id];
      let cost = el?.unit_cost;
      if (cost == null) {
        try { cost = (await mfgApi.item(ln.item_id))?.cost ?? 0; } catch { cost = 0; }
      }
      return {
        line_id: ln.line_id, item_id: ln.item_id, name: ln.name, sku: ln.sku, qty: ln.qty, uom: ln.uom,
        cost: cost ?? 0,
        price: el && el.price_source === 'manual' ? String(el.unit_price) : '',
      };
    })).then(setRows).finally(() => setLoading(false));
  }, [open, order, settings]);

  const computed = useMemo(() => {
    const m = Number(marginPct) || 0;
    const lines = rows.map((r) => {
      const cost = Number(r.cost) || 0;
      const pl = r.price === '' ? plMap[r.item_id] : undefined;
      const price = r.price !== '' ? (Number(r.price) || 0) : (pl != null ? pl : round(cost * (1 + m / 100)));
      return { ...r, effPrice: price, plUsed: pl != null && r.price === '', amount: round(price * (Number(r.qty) || 0)) };
    });
    const subtotal = round(lines.reduce((s, l) => s + l.amount, 0));
    const tax = Math.floor(subtotal * (Number(taxRate) || 0) / 100);
    return { lines, subtotal, tax, total: subtotal + tax };
  }, [rows, marginPct, taxRate, plMap]);

  const belowMin = settings && Number(marginPct) < Number(settings.min_margin_pct || 0);

  const save = async () => {
    setSaving(true);
    try {
      const overrides = rows.map((r) => ({
        line_id: r.line_id,
        unit_cost: Number(r.cost) || 0,
        ...(r.price !== '' ? { unit_price: Number(r.price) || 0 } : {}),
      }));
      const updated = await mktApi.estimate(order.id, { margin_pct: Number(marginPct), tax_rate: Number(taxRate), overrides });
      toast.success('見積を計算・保存しました');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mkt-estimate-dialog">
        <DialogHeader><DialogTitle>見積計算 — {order?.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs font-medium text-zinc-600">マージン率 (%)</label>
              <Input type="number" step="any" value={marginPct} onChange={(e) => setMarginPct(e.target.value)}
                className="mt-1 w-32 text-right" data-testid="mkt-estimate-margin" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">消費税率 (%)</label>
              <Input type="number" step="any" value={taxRate} onChange={(e) => setTaxRate(e.target.value)}
                className="mt-1 w-28 text-right" data-testid="mkt-estimate-tax" />
            </div>
            {belowMin && (
              <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-700" data-testid="mkt-estimate-margin-warning">
                <WarningDiamond size={14} weight="fill" />
                最低マージン率（{settings.min_margin_pct}%）未満のため、見積書発行には上長承認が必要になります
              </div>
            )}
          </div>

          {loading ? (
            <div className="py-8 text-center text-zinc-400"><CircleNotch size={20} className="inline animate-spin" /></div>
          ) : (
            <div className="rounded-lg border border-zinc-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] text-zinc-400">
                    <th className="px-3 py-2 font-medium">品名 / 品番</th>
                    <th className="px-2 py-2 font-medium text-right">数量</th>
                    <th className="px-2 py-2 font-medium text-right">原価</th>
                    <th className="px-2 py-2 font-medium text-right">単価（空欄=自動）</th>
                    <th className="px-3 py-2 font-medium text-right">金額</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.lines.map((r, idx) => (
                    <tr key={r.line_id} className="border-b border-zinc-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-zinc-800">{r.name}</div>
                        <div className="flex items-center gap-2">
                          {r.sku && <span className="font-mono text-[10px] text-zinc-400">{r.sku}</span>}
                          {(Number(r.cost) || 0) === 0 && <span className="rounded bg-red-50 px-1.5 text-[10px] text-red-500">原価未設定</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-xs tabular-nums">{r.qty} {r.uom}</td>
                      <td className="px-2 py-2 text-right">
                        <Input type="number" step="any" value={r.cost}
                          onChange={(e) => setRows((p) => p.map((x, i) => (i === idx ? { ...x, cost: e.target.value } : x)))}
                          className="h-8 w-24 text-right ml-auto" data-testid={`mkt-estimate-cost-${idx}`} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input type="number" step="any" value={r.price} placeholder={String(r.effPrice)}
                          onChange={(e) => setRows((p) => p.map((x, i) => (i === idx ? { ...x, price: e.target.value } : x)))}
                          className="h-8 w-28 text-right ml-auto" data-testid={`mkt-estimate-price-${idx}`} />
                        {r.plUsed && <div className="mt-0.5 text-[9px] font-semibold text-fuchsia-500" data-testid={`mkt-estimate-pl-badge-${idx}`}>単価マスタ適用</div>}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">{yen(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-zinc-600"><span>小計</span><span className="tabular-nums" data-testid="mkt-estimate-subtotal">{yen(computed.subtotal)}</span></div>
              <div className="flex justify-between text-zinc-600"><span>消費税 ({taxRate}%)</span><span className="tabular-nums">{yen(computed.tax)}</span></div>
              <div className="flex justify-between border-t border-zinc-200 pt-1.5 font-bold text-zinc-900"><span>合計</span><span className="tabular-nums" data-testid="mkt-estimate-total">{yen(computed.total)}</span></div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mkt-estimate-cancel-btn">キャンセル</Button>
            <Button onClick={save} disabled={saving || loading} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-estimate-save-btn">
              {saving ? '保存中…' : '計算して保存'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
